import React, { createContext, useState, useContext, useEffect } from 'react';
import axios from 'axios';
import i18n from '../i18n';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(
    localStorage.getItem('token') || sessionStorage.getItem('token')
  );

  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

  const fetchUser = async () => {
    try {
      const response = await axios.get(`${API_URL}/auth/profile/`);
      setUser(response.data);
      // Mettre à jour la langue si nécessaire
      if (response.data.langue) {
        i18n.changeLanguage(response.data.langue);
        localStorage.setItem('language', response.data.langue);
      }
    } catch (error) {
      console.error('Error fetching user:', error);
      setToken(null);
      setUser(null);
      localStorage.removeItem('token');
      sessionStorage.removeItem('token');
      delete axios.defaults.headers.common['Authorization'];
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    try {
      await axios.post(`${API_URL}/auth/logout/`);
    } catch (error) {
      console.error('Error logging out:', error);
    } finally {
      setToken(null);
      setUser(null);
      localStorage.removeItem('token');
      sessionStorage.removeItem('token');
      delete axios.defaults.headers.common['Authorization'];
    }
  };

  // Vérifier d'abord le token d'impersonation dans l'URL (une seule fois au montage)
  useEffect(() => {
    // Vérifier si un token d'impersonation est présent dans l'URL
    const urlParams = new URLSearchParams(window.location.search);
    const impersonateToken = urlParams.get('impersonate_token');
    
    if (impersonateToken) {
      // Stocker le token d'impersonation
      sessionStorage.setItem('token', impersonateToken);
      setToken(impersonateToken);
      axios.defaults.headers.common['Authorization'] = `Token ${impersonateToken}`;
      
      // Nettoyer l'URL immédiatement
      window.history.replaceState({}, '', window.location.pathname);
      
      // Charger les données utilisateur
      const loadUser = async () => {
        try {
          const response = await axios.get(`${API_URL}/auth/profile/`);
          setUser(response.data);
          if (response.data.langue) {
            i18n.changeLanguage(response.data.langue);
            localStorage.setItem('language', response.data.langue);
          }
        } catch (error) {
          console.error('Error fetching user:', error);
          setToken(null);
          setUser(null);
          localStorage.removeItem('token');
          sessionStorage.removeItem('token');
          delete axios.defaults.headers.common['Authorization'];
        } finally {
          setLoading(false);
        }
      };
      
      loadUser();
    } else if (token) {
      // Token existant dans le storage
      axios.defaults.headers.common['Authorization'] = `Token ${token}`;
      fetchUser();
    } else {
      // Pas de token
      setLoading(false);
    }
  }, []); // Exécuter une seule fois au montage

  // Effet séparé pour gérer les changements de token après le montage initial
  useEffect(() => {
    if (token && !loading) {
      axios.defaults.headers.common['Authorization'] = `Token ${token}`;
    }
  }, [token, loading]);

  const login = async (email, password, rememberMe = false) => {
    try {
      const response = await axios.post(`${API_URL}/auth/login/`, {
        email,
        password,
      });
      const { token: newToken, user: userData } = response.data;
      setToken(newToken);
      setUser(userData);
      
      // Stocker le token selon la préférence de l'utilisateur
      if (rememberMe) {
        localStorage.setItem('token', newToken);
        sessionStorage.removeItem('token');
      } else {
        sessionStorage.setItem('token', newToken);
        localStorage.removeItem('token');
      }
      
      axios.defaults.headers.common['Authorization'] = `Token ${newToken}`;
      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data || { message: 'Erreur de connexion' },
      };
    }
  };

  const signup = async (userData) => {
    try {
      console.log('Sending signup request to:', `${API_URL}/auth/register/`);
      const response = await axios.post(`${API_URL}/auth/register/`, userData);
      console.log('Signup response:', response.data);
      // L'inscription réussit même si l'email n'est pas encore vérifié
      // On ne stocke pas le token car l'utilisateur doit d'abord vérifier son email
      return { success: true, data: response.data };
    } catch (error) {
      console.error('Signup error:', error);
      console.error('Error response:', error.response?.data);
      return {
        success: false,
        error: error.response?.data || { message: 'Erreur d\'inscription' },
      };
    }
  };

  // Vérifier l'authentification en tenant compte du token dans le storage aussi
  const isAuthenticated = !!token || !!(localStorage.getItem('token') || sessionStorage.getItem('token'));

  const refreshUser = async () => {
    if (token) {
      await fetchUser();
    }
  };

  const value = {
    user,
    loading,
    login,
    signup,
    logout,
    refreshUser,
    isAuthenticated,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
